import {
  useCallback,
  useDeferredValue,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronRight, Search, X } from 'lucide-react';
import clsx from 'clsx';
import AppIcon from './AppIcon';
import {
  buildFullIndex,
  buildMetaIndex,
  highlightParts,
  queryStems,
  searchDocs,
  type SearchEntry,
  type SearchHit,
} from '@/lib/docsSearch';

const META_INDEX = buildMetaIndex();

let fullIndex: Promise<SearchEntry[]> | null = null;
function loadFullIndex(): Promise<SearchEntry[]> {
  fullIndex ??= import('@/data/docs/content').then((m) => buildFullIndex(m.DOC_CONTENT));
  return fullIndex;
}

function useDocsSearch() {
  const [query, setQuery] = useState('');
  const [index, setIndex] = useState<SearchEntry[]>(META_INDEX);
  const deferred = useDeferredValue(query);
  const hits = useMemo(() => searchDocs(index, deferred), [index, deferred]);
  const stems = useMemo(() => queryStems(deferred), [deferred]);
  const warm = useCallback(() => {
    loadFullIndex().then(setIndex, () => {
      fullIndex = null;
    });
  }, []);
  return { query, setQuery, hits, stems, warm };
}

function Highlighted({ text, stems }: { text: string; stems: string[] }) {
  return (
    <>
      {highlightParts(text, stems).map((part, i) =>
        part.hit ? (
          <mark
            key={i}
            className="rounded-[3px] bg-orange-50 px-px text-inherit [-webkit-box-decoration-break:clone] [box-decoration-break:clone]"
          >
            {part.text}
          </mark>
        ) : (
          part.text
        ),
      )}
    </>
  );
}

function ResultItem({
  id,
  hit,
  stems,
  active,
  onHover,
  onSelect,
}: {
  id?: string;
  hit: SearchHit;
  stems: string[];
  active?: boolean;
  onHover?: () => void;
  onSelect?: () => void;
}) {
  return (
    <li role="presentation">
      <Link
        id={id}
        role="option"
        aria-selected={active}
        to={hit.href}
        onMouseMove={onHover}
        onClick={onSelect}
        className={clsx(
          'flex items-start gap-3 px-4 py-2.5 transition-colors focus-visible:outline-none',
          active ? 'bg-ink/[0.05]' : 'hover:bg-ink/[0.035]',
        )}
      >
        <AppIcon slug={hit.entry.slug} size={28} className="mt-0.5" />
        <span className="min-w-0">
          <span className="block text-[14.5px] font-medium leading-snug text-ink">
            <Highlighted text={hit.entry.title} stems={stems} />
            {hit.entry.section ? (
              <>
                <ChevronRight aria-hidden size={14} className="mx-1 inline-block -translate-y-px text-ink/35" />
                <Highlighted text={hit.entry.section} stems={stems} />
              </>
            ) : null}
          </span>
          <span className="mt-0.5 line-clamp-2 text-[13.5px] leading-snug text-ink/65">
            <Highlighted text={hit.snippet} stems={stems} />
          </span>
        </span>
      </Link>
    </li>
  );
}

function NoResults({ query }: { query: string }) {
  return (
    <p className="px-5 py-6 text-[14.5px] leading-relaxed text-ink/70">
      По запросу «{query.trim()}» ничего не нашлось. Попробуйте сказать иначе или{' '}
      <Link to="/support" className="text-blue-700 underline decoration-blue-700/30 underline-offset-[3px] hover:decoration-blue-700">
        напишите в поддержку
      </Link>
      .
    </p>
  );
}

interface FieldProps {
  variant: 'hero' | 'header';
  examples?: string[];
}

/** Поле поиска с выпадающими результатами: большое на главной документации, компактное в шапке. */
export function DocsSearchField({ variant, examples }: FieldProps) {
  const { query, setQuery, hits, stems, warm } = useDocsSearch();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { pathname, hash } = useLocation();
  const inputId = useId();
  const listId = useId();
  const hero = variant === 'hero';
  const showPanel = open && query.trim().length > 0;

  useEffect(() => {
    setOpen(false);
    setQuery('');
  }, [pathname, hash, setQuery]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      if (target && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName))) return;
      if (!inputRef.current || inputRef.current.offsetParent === null) return;
      e.preventDefault();
      inputRef.current.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open]);

  useEffect(() => {
    if (showPanel) document.getElementById(`${listId}-${active}`)?.scrollIntoView({ block: 'nearest' });
  }, [active, showPanel, listId]);

  const onKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActive((i) => Math.min(i + 1, Math.max(hits.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      const hit = hits[active];
      if (!hit) return;
      e.preventDefault();
      inputRef.current?.blur();
      navigate(hit.href);
    } else if (e.key === 'Escape') {
      if (showPanel) setOpen(false);
      else if (query) setQuery('');
      else inputRef.current?.blur();
    }
  };

  const fill = (value: string) => {
    setQuery(value);
    setActive(0);
    setOpen(true);
    warm();
    inputRef.current?.focus();
  };

  return (
    <div ref={rootRef} className="relative">
      <label htmlFor={inputId} className="sr-only">
        Поиск по документации
      </label>
      <Search
        aria-hidden
        strokeWidth={2}
        className={clsx(
          'pointer-events-none absolute top-1/2 -translate-y-1/2 text-ink/45',
          hero ? 'left-[18px] h-5 w-5' : 'left-3 h-4 w-4',
        )}
      />
      <input
        ref={inputRef}
        id={inputId}
        type="search"
        role="combobox"
        aria-expanded={showPanel}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={showPanel && hits[active] ? `${listId}-${active}` : undefined}
        autoComplete="off"
        spellCheck={false}
        enterKeyHint="search"
        placeholder={hero ? 'Что нужно сделать? Например, подключить кассу' : 'Поиск'}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setActive(0);
          setOpen(true);
        }}
        onFocus={() => {
          warm();
          setOpen(true);
        }}
        onKeyDown={onKeyDown}
        className={clsx(
          'w-full appearance-none border text-ink caret-orange outline-none transition-[border-color,box-shadow,background-color] duration-200 focus-visible:outline-none [&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden',
          hero
            ? 'h-14 rounded-2xl border-ink/15 bg-white pl-[52px] pr-14 text-[16.5px] shadow-[0_1px_2px_rgba(14,23,71,0.05)] placeholder:text-ink/60 focus:border-ink/35 focus:shadow-[0_0_0_4px_rgba(232,86,36,0.14)]'
            : 'h-10 rounded-xl border-ink/[0.1] bg-ink/[0.035] pl-9 pr-10 text-[14.5px] placeholder:text-ink/65 focus:border-ink/30 focus:bg-white focus:shadow-[0_0_0_3px_rgba(232,86,36,0.14)]',
        )}
      />
      {query ? (
        <button
          type="button"
          aria-label="Очистить поиск"
          onClick={() => {
            setQuery('');
            inputRef.current?.focus();
          }}
          className={clsx(
            'absolute top-1/2 grid -translate-y-1/2 place-items-center rounded-lg text-ink/50 transition-colors hover:bg-ink/5 hover:text-ink',
            hero ? 'right-3 h-9 w-9' : 'right-1.5 h-7 w-7',
          )}
        >
          <X size={hero ? 18 : 15} strokeWidth={2} />
        </button>
      ) : (
        <kbd
          aria-hidden
          className={clsx(
            'pointer-events-none absolute top-1/2 hidden -translate-y-1/2 place-items-center rounded-md border border-ink/15 font-sans font-medium text-ink/55 sm:grid',
            hero ? 'right-4 h-7 w-7 text-[13px]' : 'right-2.5 h-5 w-5 text-[11px]',
          )}
        >
          /
        </kbd>
      )}

      {showPanel && (
        <div
          id={listId}
          className={clsx(
            'absolute top-full z-50 mt-2 overflow-hidden rounded-2xl bg-white shadow-[0_0_0_1px_rgba(14,23,71,0.07),0_24px_48px_-20px_rgba(14,23,71,0.3)] motion-safe:animate-[docs-pop_160ms_cubic-bezier(0.16,1,0.3,1)]',
            hero ? 'inset-x-0' : 'right-0 w-[min(36rem,calc(100vw-2rem))]',
          )}
        >
          {hits.length > 0 ? (
            <ul role="listbox" aria-label="Результаты поиска" className="max-h-[min(28rem,65vh)] overflow-y-auto py-2">
              {hits.map((hit, i) => (
                <ResultItem
                  key={hit.href}
                  id={`${listId}-${i}`}
                  hit={hit}
                  stems={stems}
                  active={i === active}
                  onHover={() => setActive(i)}
                />
              ))}
            </ul>
          ) : (
            <NoResults query={query} />
          )}
        </div>
      )}

      {examples && examples.length > 0 && (
        <p className="mt-3.5 flex flex-wrap items-center gap-x-1.5 gap-y-2 text-[14px] text-ink/60">
          <span className="mr-1">Например:</span>
          {examples.map((example) => (
            <button
              key={example}
              type="button"
              onClick={() => fill(example)}
              className="rounded-lg bg-ink/[0.045] px-2.5 py-1 text-ink/80 transition-colors hover:bg-ink/[0.08] hover:text-ink"
            >
              {example}
            </button>
          ))}
        </p>
      )}
    </div>
  );
}

/** На телефоне шапке не хватает места на поле: кнопка открывает поиск на весь экран. */
export function DocsSearchSheetButton() {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Поиск по документации"
        className="grid h-10 w-10 place-items-center rounded-xl text-ink/75 transition-colors hover:bg-ink/5 hover:text-ink md:hidden"
      >
        <Search size={20} strokeWidth={1.9} />
      </button>
      {open && typeof document !== 'undefined' && createPortal(<SearchSheet onClose={close} />, document.body)}
    </>
  );
}

function SearchSheet({ onClose }: { onClose: () => void }) {
  const { query, setQuery, hits, stems, warm } = useDocsSearch();
  const navigate = useNavigate();

  useEffect(() => {
    warm();
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKey);
    };
  }, [warm, onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Поиск по документации"
      className="fixed inset-0 z-[100] flex flex-col bg-white md:hidden"
    >
      <div className="flex items-center gap-2 border-b border-ink/[0.08] px-3 py-2.5">
        <div className="relative flex-1">
          <Search aria-hidden size={18} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink/45" />
          <input
            autoFocus
            type="search"
            aria-label="Поиск по документации"
            autoComplete="off"
            spellCheck={false}
            enterKeyHint="search"
            placeholder="Что нужно сделать?"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && hits[0]) {
                onClose();
                navigate(hits[0].href);
              }
            }}
            className="h-11 w-full appearance-none rounded-xl bg-ink/[0.045] pl-10 pr-3 text-[16px] text-ink caret-orange outline-none placeholder:text-ink/65 focus-visible:outline-none [&::-webkit-search-cancel-button]:hidden"
          />
        </div>
        <button type="button" onClick={onClose} className="rounded-lg px-2.5 py-2 text-[15px] font-medium text-ink/70">
          Отмена
        </button>
      </div>
      <div className="flex-1 overflow-y-auto overscroll-contain">
        {query.trim() ? (
          hits.length > 0 ? (
            <ul role="listbox" aria-label="Результаты поиска" className="py-2">
              {hits.map((hit) => (
                <ResultItem key={hit.href} hit={hit} stems={stems} onSelect={onClose} />
              ))}
            </ul>
          ) : (
            <NoResults query={query} />
          )
        ) : (
          <p className="px-5 py-6 text-[14.5px] leading-relaxed text-ink/60">
            Напишите, что нужно сделать: например, «закрыть смену» или «подключить принтер».
          </p>
        )}
      </div>
    </div>
  );
}
